import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
} from "./ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { SelectProps } from "../forms/form-types";
import { InputWrapper, INVALID_CONTROL_CLASS } from "./input-wrapper";

interface CustomAutocompleteShadcnProps extends SelectProps {
	options: {
		value: string | number;
		label: string;
	}[];
	inputLabel: string;
	isMultiple?: boolean;
	helperText?: string;
	className?: string;
	allowFreeText?: boolean;
	/** Red border without the message, for composites that render one message for all parts. */
	invalid?: boolean;
}

const FREE_TEXT_PREFIX = "__freetext__|||";
const CLEAR_VALUE = "__clear__";

export const CustomAutocompleteShadcn: React.FC<
	CustomAutocompleteShadcnProps
> = ({
	options,
	inputLabel,
	value,
	onChange,
	disabled = false,
	required = false,
	helperText = "",
	className,
	inputHeight,
	allowFreeText = false,
	validationError,
	invalid = !!validationError,
}) => {
	const [open, setOpen] = React.useState(false);
	const [inputValue, setInputValue] = React.useState("");

	const trimmedInput = inputValue.trim();
	const hasExactMatch = options.some(
		(o) => o.label.toLowerCase() === trimmedInput.toLowerCase()
	);
	const showFreeTextOption =
		allowFreeText && trimmedInput.length > 0 && !hasExactMatch;

	const getCurrentOption = () => {
		const option = options.find((o) => o.value === value);
		return option ? option.label : value?.toString() || "";
	};

	const handleInputChange = (newValue: string) => {
		setInputValue(newValue);
	};

	const customFilter = (value: string, search: string) => {
		if (value === CLEAR_VALUE) return 1;
		if (value.startsWith(FREE_TEXT_PREFIX)) return 1;
		if (!search.trim()) return 1;

		const normalizeString = (str: string) =>
			str
				.toLowerCase()
				.replace(/[^\w\s]/g, " ")
				.trim()
				.split(/\s+/)
				.filter((word) => word);

		const searchWords = normalizeString(search);
		const optionWords = normalizeString(value);

		const matches = searchWords.every((searchWord) =>
			optionWords.some(
				(optionWord) =>
					optionWord.includes(searchWord) ||
					searchWord.includes(optionWord)
			)
		);

		return matches ? 1 : 0;
	};

	const commitFreeText = () => {
		onChange(trimmedInput);
		setInputValue("");
		setOpen(false);
	};

	const handleClear = () => {
		onChange("");
		setInputValue("");
		setOpen(false);
	};

	const handleSelect = (selectedValue: string) => {
		if (selectedValue === CLEAR_VALUE) {
			handleClear();
			return;
		}
		if (selectedValue.startsWith(FREE_TEXT_PREFIX)) {
			const freeText = selectedValue.slice(FREE_TEXT_PREFIX.length);
			onChange(freeText);
			setInputValue("");
			setOpen(false);
			return;
		}
		const [value] = selectedValue.split("|||");
		if (value) {
			onChange(value);
		}
		setInputValue("");
		setOpen(false);
	};

	const handleInputKeyDown = (
		event: React.KeyboardEvent<HTMLInputElement>
	) => {
		if (
			allowFreeText &&
			event.key === "Enter" &&
			!event.nativeEvent.isComposing &&
			trimmedInput.length > 0 &&
			!hasExactMatch
		) {
			event.preventDefault();
			event.stopPropagation();
			commitFreeText();
		}
	};

	return (
		<InputWrapper
			label={inputLabel}
			required={required}
			customHelperText={helperText}
			className={className}
			validationError={validationError}
		>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						role="combobox"
						aria-expanded={open}
						aria-invalid={invalid}
						className={cn(
							"w-full justify-between font-normal",
							"h-10",
							disabled && "opacity-50 cursor-not-allowed",
							invalid && INVALID_CONTROL_CLASS
						)}
						disabled={disabled}
						style={
							inputHeight
								? { height: `${inputHeight}px` }
								: undefined
						}
					>
						<span className="truncate flex-1 text-left">
							{getCurrentOption() || `${inputLabel} auswählen...`}
						</span>
						<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-full p-0">
					<Command filter={customFilter}>
						<CommandInput
							placeholder={`Suche nach ${inputLabel}...`}
							value={inputValue}
							onValueChange={handleInputChange}
							onKeyDown={handleInputKeyDown}
						/>
						<CommandEmpty>Keine Ergebnisse gefunden</CommandEmpty>
						<CommandGroup className="max-h-[calc(30vh)] overflow-y-auto">
							{value && (
								<CommandItem
									key="__clear__"
									value={CLEAR_VALUE}
									onSelect={handleSelect}
									className="text-red-600 data-[selected=true]:bg-red-50 data-[selected=true]:text-red-700"
								>
									<X className="mr-2 h-4 w-4" />
									<span>Leeren</span>
								</CommandItem>
							)}
							{options.map((option) => (
								<CommandItem
									key={option.value.toString()}
									value={`${option.value}|||${option.label}`}
									onSelect={handleSelect}
								>
									<Check
										className={cn(
											"mr-2 h-4 w-4",
											value === option.value
												? "opacity-100"
												: "opacity-0"
										)}
									/>
									{option.label}
								</CommandItem>
							))}
							{showFreeTextOption && (
								<CommandItem
									key="__freetext__"
									value={`${FREE_TEXT_PREFIX}${trimmedInput}`}
									onSelect={handleSelect}
								>
									<span className="mr-2 h-4 w-4" />
									<span className="text-muted-foreground">
										Hinzufügen:&nbsp;
									</span>
									<span>&quot;{trimmedInput}&quot;</span>
								</CommandItem>
							)}
						</CommandGroup>
					</Command>
				</PopoverContent>
			</Popover>
			{helperText && (
				<p className="text-sm text-muted-foreground">{helperText}</p>
			)}
		</InputWrapper>
	);
};
